import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Person,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { LEAD_STATUS_LABELS } from '../../services/leads';

const StatusHistory = ({ statusHistory = [], currentStatus }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'info',
      'warm': 'warning',
      'hot': 'error',
      'followup': 'warning',
      'ipd_done': 'success',
      'opd_done': 'success',
      'converted': 'success',
      'dnp': 'error',
      'dead': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (oldStatus, newStatus) => {
    // Define status hierarchy for determining if it's an improvement
    const statusHierarchy = {
      'new': 1,
      'warm': 2,
      'hot': 3,
      'followup': 2.5,
      'opd_done': 4,
      'ipd_done': 5,
      'converted': 6,
      'dnp': 0,
      'dead': 0
    };

    const oldLevel = statusHierarchy[oldStatus] || 0;
    const newLevel = statusHierarchy[newStatus] || 0;

    if (newStatus === 'converted') return <CheckCircle color="success" />;
    if (newStatus === 'dnp') return <RadioButtonUnchecked color="error" />;
    if (newStatus === 'dead') return <RadioButtonUnchecked color="error" />;
    if (newLevel > oldLevel) return <TrendingUp color="success" />;
    if (newLevel < oldLevel) return <TrendingDown color="warning" />;
    return <RadioButtonUnchecked color="action" />;
  };

  if (!statusHistory || statusHistory.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TimelineIcon color="primary" />
            <Typography variant="h6">Status History</Typography>
          </Box>
          <Alert severity="info">
            No status changes recorded for this lead.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <TimelineIcon color="primary" />
          <Typography variant="h6">Status History</Typography>
          <Chip label={`${statusHistory.length} changes`} size="small" />
        </Box>

        <List>
          {statusHistory.map((statusChange, index) => (
            <React.Fragment key={statusChange.id || index}>
              <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemIcon sx={{ mt: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: index === 0 ? 'primary.main' : 'grey.500' }}>
                    {getStatusIcon(statusChange.old_status, statusChange.new_status)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ p: 2, bgcolor: index === 0 ? 'primary.50' : 'grey.50', borderRadius: 1 }}>
                      {/* Status Change Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Status Updated
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(statusChange.changed_at)}
                          </Typography>
                        </Box>
                        {index === 0 && (
                          <Chip label="Latest" color="primary" size="small" />
                        )}
                      </Box>

                      {/* Status Change Flow */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {statusChange.old_status && (
                          <>
                            <Chip
                              label={LEAD_STATUS_LABELS[statusChange.old_status] || statusChange.old_status}
                              color={getStatusColor(statusChange.old_status)}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body2" color="text.secondary">→</Typography>
                          </>
                        )}

                        <Chip
                          label={LEAD_STATUS_LABELS[statusChange.new_status] || statusChange.new_status}
                          color={getStatusColor(statusChange.new_status)}
                          size="small"
                        />
                      </Box>

                      {/* Changed By */}
                      {statusChange.changed_by_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar sx={{ width: 20, height: 20 }}>
                            <Person fontSize="small" />
                          </Avatar>
                          <Typography variant="body2">
                            Changed by: <strong>{statusChange.changed_by_name}</strong>
                          </Typography>
                        </Box>
                      )}

                      {/* Status Context */}
                      <Box sx={{ mt: 1 }}>
                        {statusChange.new_status === 'converted' && (
                          <Alert severity="success" sx={{ py: 0.5 }}>
                            🎉 Lead successfully converted!
                          </Alert>
                        )}
                        {statusChange.new_status === 'dead' && (
                          <Alert severity="error" sx={{ py: 0.5 }}>
                            Lead marked as dead - no further action needed
                          </Alert>
                        )}
                        {statusChange.new_status === 'followup' && (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            Follow-up required - check scheduled date
                          </Alert>
                        )}
                        {(statusChange.new_status === 'ipd_done' || statusChange.new_status === 'opd_done') && (
                          <Alert severity="success" sx={{ py: 0.5 }}>
                            Treatment completed successfully
                          </Alert>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
              {index < statusHistory.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
        </List>

        {/* Current Status Summary */}
        {currentStatus && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Status
            </Typography>
            <Chip
              label={LEAD_STATUS_LABELS[currentStatus] || currentStatus}
              color={getStatusColor(currentStatus)}
              size="medium"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StatusHistory;